import { themePackStorage } from "@utils/onboarding/themePackStorage";
import { resolveChatHandoff } from "@/src/utils/onboarding/chatHandoffReference";
import { Track } from "@/src/lib/tracking";
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { DrawerProps } from "antd";
import { cn, pubsub } from "utils";
import {
  deviceDetector,
  Icon,
  ospThemes,
  lazyHydrate,
  VirtualList,
  type VirtualListRef,
} from "@ospweb/sdk-ui";
import { PullToRefresh } from "@components/PullToRefresh";
import { useMemoizedFn } from "ahooks";
import { usePathname } from "next/navigation";
import { useSnapshot } from "valtio";
import Sender, { type SenderRef } from "../Sender/Sender";
import { Question, QuestionItem } from "../Sender/QuestionItem";
import { AD_CHAT_PROMPT_SCOPE } from "@hooks/useAdChatPrompts";
import {
  adChatFirstMessageSent,
  adChatPromptTap,
} from "@utils/onboarding/adChatTrack";
import { getLandingIdFromStore } from "@utils/onboarding/adSourceStore";
import { getAdSourceCookie } from "@utils/onboarding/adSourceCookie.client";
import type {
  ChatRefContent,
  SenderSendResult,
  SenderSubmitOptions,
  SenderConnectMenu,
} from "../Sender/type";
import Message from "../Chat/Message";
import {
  ChatTabHeader,
  RENEW_THRESHOLD_MINUTES,
} from "../ChatTabComp/ChatTabHeader";
import type { ChatTabMoreMenuItemId } from "../ChatTabComp/ChatTabMoreMenu";
import {
  ChatModalHeaderBar,
  CHAT_MODAL_MOBILE_HEADER_HEIGHT_PX,
} from "./ChatModalHeaderBar";
import EdgenDrawerCallable from "../EdgenDrawerCallable";
import { useDrawerCanonicalScrollContext } from "../Drawer/drawerCanonicalScrollContext";
import Translations from "@translate/components/ChatModal/ChatContent";
import { I18nUtil } from "@utils/i18n";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { ROUTER_PATH } from "@config/router";
import { IChatInfoProps } from "@hooks/useCreateChat";
import { useChatPageMessages } from "@hooks/useChatPageMessages";
import { useChats } from "@hooks/useChats";
import { useChatUpdate } from "@hooks/useChatUpdate";
import { useCredits } from "@hooks/useCredits";
import { useIsPc } from "@hooks/useIsPc";
import { useKeyboardInsets } from "@hooks/useKeyboardInsets";
import { useMemberUser } from "@hooks/useMemberUser";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { ChatStore } from "@store/modules/chat";
import { rootStore } from "@store/rootStore";
import { useChatViewReportHandler } from "@/src/hooks/useChatViewReportHandler";
import { useRecommendQuestionTracking } from "@/src/hooks/useRecommendQuestionTracking";
import { getChatTabMoreMenuItemUiState } from "@utils/chatTabMoreMenuItemUiState";
import { decodeString, encodeString } from "@utils/stringEncoder";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { parseReferencePatternStrict } from "@utils/chatUtils/parseChatMessage";
import { mergeAndFilterInteractiveToolMessages } from "@utils/chatUtils/mergeInteractiveToolMessage";
import { useChatCreditCheck } from "@/src/hooks/useChatCreditCheck";
import { useMessageMinHeight } from "@/src/hooks/useMessageMinHeight";
import { MessageType } from "@/src/lib/bridge/types";
import { useBridge } from "@/src/lib/bridge/useBridge";
import { ThemePackChatHandoff } from "./ThemePackChatHandoff";
import { SyncHandoff } from "./SyncHandoff";
import { useOverlapEntry } from "@features/etf-overlap/hooks/useOverlapEntry";
import {
  overlapAttributionDataProps,
  overlapAttributionParams,
} from "@features/etf-overlap/tracking/overlapAttribution";
import { detectOverlapFailureKind } from "@features/etf-overlap/utils/overlapFailureKinds";
import {
  isEtfOverlapAdChat,
  rememberOverlapAdChat,
} from "@features/etf-overlap/utils/overlapAdChat";
import { themePackSessionKeys } from "@/src/utils/onboarding/themePackSessionKeys";
import {
  buildConnectEntryTrackingParams,
  type ChatConnectEntryState,
} from "@hooks/chatConnectEntry/connectEntryState";
import {
  CONNECT_ACCOUNT_SILENT_MESSAGE,
  resolveCardEntry,
} from "@hooks/chatConnectEntry/resolveCardEntry";
import { openComposerMenuUpload } from "@hooks/chatConnectEntry/openComposerMenuUpload";

/** 交互卡工具名（chatsDetail.interactive_tool_calls[0].toolName） */
const REQUEST_PORTFOLIO_TOOL = "request_portfolio";

const isRequestPortfolioMessage = (message: unknown): boolean =>
  (message as { interactiveToolCalls?: { toolName?: string }[] } | undefined)
    ?.interactiveToolCalls?.[0]?.toolName === REQUEST_PORTFOLIO_TOOL;

const ChatHistoryModalHydrated = lazyHydrate(
  () =>
    import("@components/ChatModal/ChatHistoryModal").then(
      (mod) => mod.ChatHistoryModal,
    ),
  {
    idleOptions: {
      enableIdleCallback: true,
    },
    ssr: false,
    loading: () => null,
  },
);

const RenameModalHydrated = lazyHydrate(
  () =>
    import("@components/ChatModal/RenameModal").then((mod) => mod.RenameModal),
  {
    idleOptions: {
      enableIdleCallback: true,
    },
    ssr: false,
    loading: () => null,
  },
);

const I18n = I18nUtil.createScoped(Translations);

interface ChatContentProps extends DrawerProps {
  className?: string;
  chatInfo?: IChatInfoProps;
  questions?: Question[];
  /**
   * 是否在创建 chatId 后自动提交首条消息（默认 true）
   * - false: 仅预填输入框（并支持解析 referenceId 引用）
   */
  autoSubmit?: boolean;
  /**
   * 控制顶部 Header 的渲染形态（优先级高于 hideHeader）
   * - modal: 渲染 ChatModal 样式 header（默认）
   * - tab: 渲染 ChatTab 样式 header（含 More menu）
   * - hidden: 不渲染任何 header
   */
  headerVariant?: "modal" | "tab" | "hidden";
  /** 页面复用时可隐藏顶部 Header 区域 */
  hideHeader?: boolean;
  /** 页面复用时可隐藏底部 Sender 区域（form） */
  hideFooter?: boolean;
  /**
   * 控制底部输入区渲染形态（优先级高于 hideFooter）
   * - sender: 渲染既有 Sender footer（默认）
   * - tab-input: 渲染 ChatTabPage 的 Figma 输入框 footer
   * - hidden: 不渲染任何 footer
   */
  footerVariant?: "sender" | "tab-input" | "hidden";
  updateChatContext?: (id: string) => void;
  handleNewChat?: () => void;
  createNewChat: (
    input: string,
    options?: {
      sign?: string;
      cacheQuestion?: string;
      customParams?: Record<string, any>;
    },
  ) => void;
  renewChat?: boolean;
  scene?: string;
  /** EDG-591 Chat 连接入口状态（推荐问题要数据槽 / `+` 菜单 / 大类卡归因）；不传则全部不启用 */
  connectEntry?: ChatConnectEntryState;
}

export const ChatContent = forwardRef((props: ChatContentProps, ref) => {
  const {
    chatInfo,
    handleNewChat,
    createNewChat,
    questions = [],
    updateChatContext,
    headerVariant,
    hideHeader = false,
    hideFooter = false,
    autoSubmit = true,
    onClose,
    renewChat,
    scene,
    connectEntry,
  } = props;
  const isOnborading = scene === "onboarding";
  // 「延迟交接」：autoSubmit=false + 协议行 = 先等持仓同步再发出（资料包 / ETF 重叠）。
  // 资料包专属的 session key / 埋点只认 `themeHandoffSlug`，重叠会话它恒为 null，不会写资料包的存储
  const { enter: enterOverlap } = useOverlapEntry();
  const handoff = resolveChatHandoff(chatInfo?.key);
  const themeHandoffSlug = handoff?.kind === "theme_pack" ? handoff.slug : null;
  const themeHandoff = !autoSubmit && handoff ? [chatInfo!.key!] : null;
  const {
    messages,
    input,
    appendMessage,
    appendToolMessage,
    appendSaveGoalConfirmed,
    stop,
    setInput,
    reload,
    error,
    messageIds,
    chatId,
    referId,
    chat,
    fetchChatDetailPreviousPage,
    hasChatDetailPreviousPage,
    chatDetailFetchingPreviousPage,
  } = useChats({ ...chatInfo, scope: "modal", scene });
  const chatVirtualListRef = useRef<VirtualListRef>(null);
  const scrollTopRef = useRef(0);
  const isReturningRef = useRef(false);

  const resolvedHeaderVariant: "modal" | "tab" | "hidden" =
    headerVariant ?? (hideHeader ? "hidden" : "modal");

  const modalContentText = {
    title: I18n.t("Ask Ed"),
    description: I18n.t("What's on your mind?"),
  };

  const signArr = chatInfo?.sign?.split("_") || "";
  const prevPathnameRef = useRef<string | null>(null);
  const pathname = usePathname();
  const isModal = resolvedHeaderVariant === "modal";

  // 推荐问题跟踪 hook
  const {
    value: trackingValue,
    setValue: setTrackingValue,
    handleChange: handleTrackingChange,
    handleQuestionSelect,
    shouldUseRecommendInfo,
    getRecommendInfo,
    reset: resetTracking,
  } = useRecommendQuestionTracking();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const themeOwnerId = ownerInfo?.id;

  const { loading: isLoading, showRecommendQuestion } = useSnapshot(ChatStore);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // credit流式输出过程中不足
  const isHiddenLoadingRef = useRef(false);

  useEffect(() => {
    try {
      isHiddenLoadingRef.current =
        localStorage.getItem(LOCAL_STORAGE_KEYS.HIDDEN_CHAT_ID) === chatId;
    } catch {
      /* ignore */
    }
  }, [chatId]);
  const senderRef = useRef<SenderRef>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  /** focusin 时在底部：等 keyboard-did-show 后再滚到底 */
  const pendingScrollOnKeyboardShowRef = useRef(false);
  const pendingSessionScrollChatIdRef = useRef<string | null>(null);
  const router = useShellRouter();
  const [isPc] = useIsPc();
  const { data: memberInfo } = useMemberUser();
  const { handleUpdateChat, isLoading: isStarLoading } = useChatUpdate();
  const { checkCredit } = useChatCreditCheck();
  const { credits } = useCredits();
  const mobileMode = deviceDetector.isMobile();
  const drawerCanonicalScroll = useDrawerCanonicalScrollContext();
  const sheetListScrollCoordination =
    mobileMode && drawerCanonicalScroll.mode === "active";
  const resolvedFooterVariant: "sender" | "tab-input" | "hidden" =
    props.footerVariant ?? (hideFooter ? "hidden" : "sender");

  const [chatRefContent, setChatRefContent] = useState<ChatRefContent | null>(
    null,
  );
  /**
   * Sender 展示层文案；与 useChats.input（发送/引用格式化）分离。
   * autoSubmit 初始化时 input 保留纯问题，displayInput 为空，避免 reference 特殊字符闪现。
   */
  const [displayInput, setDisplayInput] = useState("");
  const syncSenderFields = useMemoizedFn((value: string) => {
    setInput(value);
    setDisplayInput(value);
  });
  const clearSenderFields = useMemoizedFn(() => {
    setInput("");
    setDisplayInput("");
  });
  const [showScrollToBottomIcon, setShowScrollToBottomIcon] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const remainMinutes = Number(memberInfo?.remain_minutes ?? 0);
  const shouldShowRenew =
    !memberInfo?.auto_renewal &&
    remainMinutes > 0 &&
    remainMinutes <= RENEW_THRESHOLD_MINUTES;

  const keyboardInsets = useKeyboardInsets();
  const { on: onBridgeMessage } = useBridge();
  const useWebKeyboardInset = keyboardInsets.shouldApplyWebInsetPadding;

  const handleCloseChatToast = useMemoizedFn(() => {
    setChatRefContent(null);
  });

  useLayoutEffect(() => {
    if (drawerCanonicalScroll.mode !== "active") {
      return;
    }
    const { registerCanonicalScrollRoot } = drawerCanonicalScroll;
    registerCanonicalScrollRoot(scrollContainerRef.current);
    return () => {
      registerCanonicalScrollRoot(null);
    };
  }, [drawerCanonicalScroll, chatId]);

  useEffect(() => {
    if (!questions?.length) {
      return;
    }
    try {
      Track.log("info", "chatmodal", "recommend questions", {
        cause: String(JSON.stringify(questions)),
      });
    } catch {
      Track.log("info", "chatmodal", "recommend questions error", {
        cause: String(questions),
      });
    }
  }, [questions]);

  // 同步 tracking value 和 input / displayInput
  useEffect(() => {
    if (trackingValue !== input) {
      syncSenderFields(trackingValue);
    }
  }, [trackingValue]);

  // 在其他页面新建后提问，不具备查询历史的能力且上下文带不过来，这种场景不具备更新能力，后续完成后更新上下文。
  // keep-alive 场景：从其他 tab 切回 chat 页面时，若 loading，清空输入框；
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    if (prev === null) {
      return;
    }
    if (prev === pathname || !pathname.includes(ROUTER_PATH.CHAT)) {
      return;
    }
    if (isLoading) {
      clearSenderFields();
    }
  }, [pathname, isLoading]);

  // 固定 key 持久化滚动位置，组件卸载时 ref 会重置，所以存 localStorage
  const CHAT_SCROLL_KEY = "chat_scroll";
  useEffect(() => {
    if (!isPc || isModal) {
      return;
    }
    // 挂载时恢复
    try {
      const saved = localStorage.getItem(CHAT_SCROLL_KEY);
      if (saved && chatId) {
        const { chatId: savedId, scrollTop } = JSON.parse(saved);
        if (savedId === chatId) {
          isReturningRef.current = true;
          // setTimeout 晚于 scrollToBottomOnce 的双 rAF 执行
          const timer = setTimeout(() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = scrollTop;
            }
            isReturningRef.current = false;
          }, 100);
          return () => clearTimeout(timer);
        }
      }
    } catch {
      /* ignore */
    }
    return () => {
      // 卸载时保存
      try {
        chatId &&
          localStorage.setItem(
            CHAT_SCROLL_KEY,
            JSON.stringify({ chatId, scrollTop: scrollTopRef.current }),
          );
      } catch {
        /* ignore */
      }
    };
  }, [chatId, isPc, isModal]);

  useEffect(() => {
    if (!chatInfo?.key) {
      setChatRefContent(null);
      return;
    }
    const parsed = parseReferencePatternStrict(chatInfo?.key);
    if (!parsed) {
      return;
    }
    const pureQuestion = parsed.question ?? "";
    setChatRefContent({
      title: parsed.referenceContent,
      refId: parsed.value,
      referenceProtocol: parsed.referenceProtocol,
      question: pureQuestion,
      autoSubmit: false,
    });
    setTrackingValue(pureQuestion);
    // Ed-first 协议消息（空正文）：提交源回退为原始协议行 —— pendingSubmitRef 持有完整
    // 协议行，input 只有等于它才会触发 autoSubmit；展示层由 displayInput 控制（保持空）。
    setInput(pureQuestion || chatInfo?.key || "");
    if (autoSubmit) {
      setChatRefContent(null);
      setDisplayInput("");
    } else {
      setDisplayInput(pureQuestion);
    }
  }, [chatInfo?.key, autoSubmit]);

  useEffect(() => {
    if (!chatId) {
      pendingSessionScrollChatIdRef.current = null;
      return;
    }

    pendingSessionScrollChatIdRef.current = chatId;
  }, [chatId]);

  /**
   * showScrollToBottomIcon：控制「回到底部」按钮的显隐。
   *
   * 显示条件：消息列表距底部超过容器高度的 1/2（即用户向上滚动超过半屏）。
   * 隐藏条件：用户滚动到接近底部，或点击按钮后 scrollToBottomOnce 执行完成。
   *
   * showScrollToBottomIconRef：与 state 保持同步的 ref 镜像，
   * 用于在 updateScrollToBottomIcon 内做值比较，只有真正发生变化时才
   * 调用 setShowScrollToBottomIcon，避免 ResizeObserver 回调中频繁
   * setState 导致的无限 re-render（Maximum update depth exceeded）。
   */
  const showScrollToBottomIconRef = useRef(false);
  const updateScrollToBottomIcon = useMemoizedFn(() => {
    const container = scrollContainerRef.current;
    const next = container
      ? container.scrollHeight - container.scrollTop - container.clientHeight >
        container.clientHeight / 2
      : false;
    // 值未变化时跳过 setState，阻断 ResizeObserver → setState → resize 的循环
    if (next !== showScrollToBottomIconRef.current) {
      showScrollToBottomIconRef.current = next;
      setShowScrollToBottomIcon(next);
    }
  });

  const scrollToBottomOnce = useMemoizedFn(
    (behavior: ScrollBehavior = "auto") => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = scrollContainerRef.current;
          if (!container) {
            return;
          }

          container.scrollTo({
            top: Math.max(container.scrollHeight - container.clientHeight, 0),
            behavior,
          });
          updateScrollToBottomIcon();
        });
      });
    },
  );

  const isScrollContainerAtBottom = useMemoizedFn((threshold = 10) => {
    const container = scrollContainerRef.current;
    if (!container) {
      return false;
    }
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      threshold
    );
  });

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottomOnce,
    }),
    [scrollToBottomOnce],
  );

  // 每次 focus 后等 keyboard-did-show（不依赖 visible state，避免二次聚焦不触发）
  useEffect(() => {
    if (!mobileMode || !keyboardInsets.bridgeEnabled || isLoading) {
      return;
    }
    const flushPendingScroll = () => {
      if (!pendingScrollOnKeyboardShowRef.current) {
        return;
      }
      pendingScrollOnKeyboardShowRef.current = false;
      scrollToBottomOnce();
    };
    const unsubDidShow = onBridgeMessage(
      MessageType.KEYBOARD_DID_SHOW,
      flushPendingScroll,
    );
    return unsubDidShow;
  }, [
    mobileMode,
    keyboardInsets.bridgeEnabled,
    onBridgeMessage,
    scrollToBottomOnce,
    isLoading,
  ]);

  const isDisable = isLoading || !messages.length;
  const disableColor = isDisable
    ? ospThemes.colors.content_tertiary
    : ospThemes.colors.content_primary;

  const { minHeight, captureBeforeSend } = useMessageMinHeight(
    isLoading,
    scrollContainerRef,
    isModal,
  );
  useChatViewReportHandler({
    onViewReport: (e) => {
      appendMessage(e);
      scrollToBottomOnce();
    },
    streamLoading: !!isLoading,
  });

  const { messages: displayMessages, messageIds: displayMessageIds } = useMemo(
    () =>
      mergeAndFilterInteractiveToolMessages(messages ?? [], messageIds ?? []),
    [messages, messageIds],
  );

  // 广告入口的重叠会话：每个会话在挂载时判定一次（`peek` 在结果曝光后才被消费，之后再读会变 false，
  // 所以不能每次渲染重算，否则 effect 会把退出键又清掉）
  const overlapAdChat = useMemo(
    () => isEtfOverlapAdChat(handoff),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatId, handoff?.kind],
  );

  useEffect(() => {
    if (!themeOwnerId || !chatId) {
      return;
    }
    const key = themePackSessionKeys.chatExitBrief(themeOwnerId);
    // 资料包会话，或广告入口打开的重叠会话（EDG-561）：返回落 Brief；其余会话清键（既有行为）
    if (themeHandoffSlug || overlapAdChat) {
      themePackStorage.setItem(key, `chat:${chatId}`);
      if (overlapAdChat) {
        rememberOverlapAdChat(chatId);
      }
    } else {
      themePackStorage.removeItem(key);
    }
    pubsub.emit(pubSubKeyMap.THEME_PACK_CHAT_CONTEXT_CHANGED);
    return () => {
      if (themePackStorage.getItem(key) === `chat:${chatId}`) {
        themePackStorage.removeItem(key);
      }
      if (themeHandoff?.[0] && ChatStore.initChat === themeHandoff[0]) {
        ChatStore.initChat = "";
      }
    };
  }, [chatId, themeHandoffSlug, themeOwnerId, overlapAdChat]);

  // 重叠分析的延迟交接：离开 / 换会话时清掉 initChat，避免同一条协议行被再次自动提交
  const overlapHandoffKey =
    handoff?.kind === "etf_overlap" ? (themeHandoff?.[0] ?? "") : "";
  useEffect(() => {
    if (!overlapHandoffKey) {
      return;
    }
    return () => {
      if (ChatStore.initChat === overlapHandoffKey) {
        ChatStore.initChat = "";
      }
    };
  }, [chatId, overlapHandoffKey]);

  useEffect(() => {
    if (
      !themeHandoffSlug ||
      !themeOwnerId ||
      !chatId ||
      !displayMessages.some(
        (item) =>
          item.role === "assistant" &&
          Boolean(String(item.content ?? "").trim()),
      )
    ) {
      return;
    }
    const key = themePackSessionKeys.chatOpen(themeOwnerId, chatId);
    if (themePackStorage.getItem(key)) {
      return;
    }
    themePackStorage.setItem(key, "1");
    themePackStorage.setItem(
      themePackSessionKeys.chatStarted(themeOwnerId, themeHandoffSlug),
      chatId,
    );
    Track.view("pack_chat_open", {
      theme_id: themeHandoffSlug,
      chat_id: chatId,
    });
  }, [chatId, displayMessages, themeHandoffSlug, themeOwnerId]);

  const { handleShareAll, handleMessageShare, onSendRelatedValue } =
    useChatPageMessages({
      messages: displayMessages,
      messageIds: displayMessageIds,
      appendMessage,
      referId,
      openAutoScroll: scrollToBottomOnce,
      chatId,
    });
  const isMark = chat?.mark === 1;
  const handleStarClick = useMemoizedFn(async () => {
    if (isStarLoading || !chatId) {
      return;
    }
    await handleUpdateChat({
      params: {
        id: chatId,
        mark: isMark ? 0 : 1,
      },
    });
  });

  const onReNewChatClick = useMemoizedFn(async () => {
    clearSenderFields();
    ChatStore.initChat = "";
    handleNewChat?.();
  });
  const onShare = useCallback(
    (selected: string[]) => handleMessageShare(selected, false),
    [handleMessageShare],
  );

  const showChatInput = !isOnborading && resolvedFooterVariant !== "hidden";

  const messageComponents = useMemo(() => {
    return displayMessages?.map((item, index) => {
      return (
        <Message
          onShare={onShare}
          key={item.id || index}
          data={item}
          showChatInput={showChatInput}
          minHeight={
            index === displayMessages.length - 1 && !showRecommendQuestion
              ? minHeight
              : 0
          }
          streamLoading={isLoading}
          isLast={index === displayMessages.length - 1}
          messageId={displayMessageIds?.[index]}
          chatId={item?.chatId || chatId}
          reload={reload}
          error={error}
          isStartAnswer={
            displayMessages[displayMessages.length - 1]?.role === "assistant"
          }
          prevMessageId={displayMessageIds?.slice(0, index) as any}
          role={item?.role}
          detailId={item?.messageId || item.id}
          nextIsAssistant={displayMessages[index + 1]?.role === "assistant"}
          prevIsUser={displayMessages[index - 1]?.role === "user"}
          onSendRelatedValue={onSendRelatedValue}
          onSubmitAskUserQuestion={appendToolMessage}
          trackingScene={scene}
          onSubmitSaveGoalConfirmed={appendSaveGoalConfirmed}
          messageLength={displayMessages.length}
          currentIndex={index}
          isTab
          isOnborading={isOnborading}
          connectCardEntry={
            connectEntry && isRequestPortfolioMessage(item)
              ? resolveCardEntry(
                  displayMessages[index - 1]?.role === "user"
                    ? String(displayMessages[index - 1]?.content ?? "")
                    : undefined,
                )
              : undefined
          }
        />
      );
    });
  }, [
    connectEntry,
    displayMessages,
    displayMessageIds,
    onShare,
    showRecommendQuestion,
    minHeight,
    isLoading,
    chatId,
    reload,
    error,
    onSendRelatedValue,
    appendToolMessage,
    appendSaveGoalConfirmed,
    showChatInput,
  ]);
  const isShowRecommendQuestion =
    (renewChat || !isLoading) &&
    !chatInfo?.key &&
    ((isPc && !messages?.length) ||
      (!isPc && (renewChat || showRecommendQuestion)));

  const renameCall = useMemoizedFn(
    (
      orgData: string,
      onSaveClick: (value: string) => Promise<boolean | undefined>,
    ) => {
      renameModalRef.current?.open(orgData, onSaveClick);
    },
  );

  const handleTabMoreRename = useMemoizedFn(() => {
    if (!chatId || isStarLoading) {
      return;
    }
    const currentTitle =
      chat && typeof chat === "object" && "title" in chat
        ? String((chat as { title?: string }).title ?? "")
        : "";
    renameCall(currentTitle, async (value: string) => {
      if (!value.trim()) {
        return false;
      }
      return await handleUpdateChat({
        params: {
          id: chatId,
          title: value,
        },
      });
    });
  });

  const onSend = useMemoizedFn(
    async (
      question?: string,
      options?: SenderSubmitOptions,
    ): Promise<SenderSendResult> => {
      const hasText = !!(question || trackingValue || input)?.trim();
      // 发送条件：必须有文字输入（trim 后非空）；附件只能作为随文字一起发送的附加信息
      if (!hasText) {
        return { sent: false, reason: "noop" };
      }
      if (scene !== "onboarding") {
        const { blocked } = await checkCredit(question);
        if (blocked) {
          return { sent: false, reason: "blocked" };
        }
      }
      // EDG-561 SC-12：上一条是「读不出」失败话术、这次随文字带附件重发 → 记一次重传（每次发送各一次）。
      // 重传入口是输入框自带的「+」，没有 `Upload again` 按钮，所以只能在发送时判断；只上报，不影响发送
      if (options?.attachments?.length) {
        const lastAssistant = [...displayMessages]
          .reverse()
          .find((m) => m.role === "assistant");
        const failure = lastAssistant
          ? detectOverlapFailureKind(String(lastAssistant.content ?? ""))
          : null;
        if (failure?.kind === "recognize") {
          Track.event("overlap_reupload_sent", {
            ...overlapAttributionParams(),
            reason: failure.reason,
          });
        }
      }
      // EDG-543 广告承接：本会话第一条消息。点问题的路径已在 trackAdChatPromptTap
      // 先发过 input_type=prompt，门面按 session 去重 → 这里的 typed 不会覆盖它。
      adChatFirstMessageSent({
        adSource: getAdSourceCookie(),
        landingId: getLandingIdFromStore(),
        inputType: "typed",
      });
      setDisplayInput("");
      // 在 isLoading 异步更新前立即撑开占位高度并定位滚动位置，消除发送后抖动
      captureBeforeSend();
      const storeChatId = localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_ID);
      const rawInput = question || trackingValue || input;
      const currentInput =
        chatRefContent?.refId && !parseReferencePatternStrict(rawInput)
          ? `${
              chatRefContent.referenceProtocol ??
              `[${chatRefContent.title || ""}](referenceId=${chatRefContent.refId})`
            }\n${rawInput}`
          : rawInput;

      if (
        themeHandoffSlug &&
        themeOwnerId &&
        chatId &&
        rawInput !== themeHandoff?.[0] &&
        themePackStorage.getItem(
          themePackSessionKeys.chatStarted(themeOwnerId, themeHandoffSlug),
        ) === chatId
      ) {
        const firstMessageKey = themePackSessionKeys.chatFirstMessage(
          themeOwnerId,
          chatId,
        );
        if (!themePackStorage.getItem(firstMessageKey)) {
          themePackStorage.setItem(firstMessageKey, "1");
          Track.event("pack_chat_first_message", {
            theme_id: themeHandoffSlug,
            chat_id: chatId,
          });
        }
      }

      if (storeChatId) {
        appendMessage({
          inputValue: currentInput,
          customParams: options
            ? {
                attachments: options.attachments,
                voiceContext: options.voiceContext,
              }
            : undefined,
        });
        scrollToBottomOnce();
        setChatRefContent(null);
        resetTracking();
        return { sent: true, reason: "sent" };
      }
      let requestParams = chatInfo?.sign
        ? { sign: chatInfo.sign }
        : ({} as any);
      const isRecommend = shouldUseRecommendInfo(currentInput);

      // 如果用户选中了推荐问题且没有修改输入，传递推荐问题信息
      if (isRecommend) {
        const selectedQuestion = getRecommendInfo();
        if (selectedQuestion) {
          !question &&
            Track.log(
              "info",
              "onChartClick",
              `[trace]${isRecommend ? "[recommend]" : ""}[sender]`,
              { cause: String(requestParams) },
            );
          const sign = chatInfo?.sign
            ? signArr[1]
              ? `${signArr[0]}_${encodeString(`${decodeString(signArr[1])}+ref#${selectedQuestion.source_id}`)}`
              : `content_chat:id#${selectedQuestion.source_id}`
            : "";
          requestParams = {
            sign,
            cacheQuestion: selectedQuestion.prompt || "",
          };
        }
      }
      createNewChat(encodeString(currentInput), {
        ...requestParams,
        customParams: {
          attachments: options?.attachments,
          voiceContext: options?.voiceContext,
        },
      });
      scrollToBottomOnce();
      setChatRefContent(null);
      resetTracking();
      return { sent: true, reason: "sent" };
    },
  );

  const submitThemePackHandoff = useMemoizedFn(async () => {
    if (!themeHandoff || !chatId || messages.length > 0) {
      return false;
    }
    return onSend(themeHandoff[0]);
  });

  const handleInputChange = useMemoizedFn(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      handleTrackingChange(e);
      syncSenderFields(e.target.value);
    },
  );

  const handleStartReached = useMemoizedFn(() => {
    if (
      !hasChatDetailPreviousPage ||
      chatDetailFetchingPreviousPage ||
      isLoading
    ) {
      return;
    }
    void chatVirtualListRef.current?.preserveScrollOnPrepend(async () => {
      await fetchChatDetailPreviousPage();
    });
  });

  const [chatHistoryVisible, setChatHistoryVisible] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const renameModalRef = useRef<any>(null);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // 滚动到最底部的时候，更新BottomIcon的状态，更新为不展示！
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    updateScrollToBottomIcon();

    const handleScroll = () => {
      scrollTopRef.current = container.scrollTop;
      updateScrollToBottomIcon();
    };

    let resizeRafId = 0;
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(resizeRafId);
      resizeRafId = requestAnimationFrame(() => {
        updateScrollToBottomIcon();
      });
    });

    container.addEventListener("scroll", handleScroll, { passive: true });
    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(resizeRafId);
      container.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [updateScrollToBottomIcon]);

  // loading过程中，更新滚动到底部icon的显示状态；chatId变更时，自动滚动到底部
  useEffect(() => {
    updateScrollToBottomIcon();
  }, [messages, isLoading, showRecommendQuestion, updateScrollToBottomIcon]);

  // 弹窗的形式打开的聊天，每次打开需要滚动到底部
  useEffect(() => {
    if (!isModal || !chatId || (isReturningRef.current && isPc)) {
      return;
    }
    scrollToBottomOnce();
  }, [isModal, chatId, isPc]);

  /**
   * 统一滚动到底部：useLayoutEffect 在浏览器 paint 之前同步执行，
   * 初始加载和切换 chat 均无需区分，直接定位避免闪烁。
   * pendingSessionScrollChatIdRef 由下方 useEffect 在 chatId 变更后更新，
   * 因 useEffect 晚于 useLayoutEffect，首帧切换时自然跳过，后续渲染再触发。
   */
  useLayoutEffect(() => {
    if (!chatId || pendingSessionScrollChatIdRef.current !== chatId) {
      return;
    }
    if (isLoading || !messages.length || isReturningRef.current) {
      return;
    }

    scrollToBottomOnce();
    pendingSessionScrollChatIdRef.current = null;
  }, [chatId, isModal, isLoading, messages.length, scrollToBottomOnce]);

  // 从二级页面返回后，清除标记，允许后续正常滚动
  useEffect(() => {
    if (!isReturningRef.current) {
      return;
    }
    isReturningRef.current = false;
  }, [messages.length]);

  const closeChatHistory = useMemoizedFn(() => {
    setChatHistoryVisible(false);
  });

  const onHistoryItemClick = useMemoizedFn((id: string) => {
    updateChatContext?.(id);
    setChatHistoryVisible(false);
  });

  const openChatHistory = useMemoizedFn(() => {
    setChatHistoryVisible(true);
  });

  useEffect(() => {
    if (isPc) {
      return;
    }
    const unsubscribe = pubsub.on(
      pubSubKeyMap.CHAT_OPEN_HISTORY_MODAL,
      openChatHistory,
    );
    return () => {
      unsubscribe();
    };
  }, [isPc, openChatHistory]);

  const handleHistoryClick = useMemoizedFn(async () => {
    await EdgenDrawerCallable.end(false);
    router.push(ROUTER_PATH.HISTORY);
  });

  const onTabMoreShare = useMemoizedFn(() => {
    if (isStarLoading || isLoading || !messages?.length) {
      return;
    }
    void handleShareAll();
  });

  const onTabMoreBookmark = useMemoizedFn(() => {
    if (isStarLoading || isLoading || !messages?.length) {
      return;
    }
    void handleStarClick();
  });
  const handleChatRenewClick = () => {
    pubsub.emit(pubSubKeyMap.OPEN_RENEW_MODAL);
  };

  const tabMoreMenuItemUi = useMemo(
    () =>
      getChatTabMoreMenuItemUiState({
        messageCount: messages?.length ?? 0,
        isLoading: !!isLoading,
        isStarLoading,
        chatId,
        isMarked: isMark,
      }),
    [messages?.length, isLoading, isStarLoading, chatId, isMark],
  );

  const handleTabMoreMenuItemClick = useMemoizedFn(
    (itemId: ChatTabMoreMenuItemId) => {
      pubsub.emit(pubSubKeyMap.COPILOT_SENDER_CLOSE_CHAT);
      switch (itemId) {
        case "bookmark":
          onTabMoreBookmark();
          break;
        case "share":
          onTabMoreShare();
          break;
        case "rename":
          handleTabMoreRename();
          break;
        case "history":
          void handleHistoryClick();
          break;
        case "new-chat":
          void onReNewChatClick();
          break;
        default:
          break;
      }
    },
  );

  const modalMoreMenuItemUi = useMemo(
    () => ({
      ...getChatTabMoreMenuItemUiState({
        messageCount: messages?.length ?? 0,
        isLoading: !!isLoading,
        isStarLoading,
        chatId,
        isMarked: isMark,
      }),
      "new-chat": { visible: false },
    }),
    [messages?.length, isLoading, isStarLoading, chatId, isMark],
  );

  const handleModalMoreMenuItemClick = useMemoizedFn(
    (itemId: ChatTabMoreMenuItemId) => {
      switch (itemId) {
        case "bookmark":
          onTabMoreBookmark();
          break;
        case "share":
          onTabMoreShare();
          break;
        case "rename":
          handleTabMoreRename();
          break;
        case "history":
          pubsub.emit(pubSubKeyMap.CHAT_OPEN_HISTORY_MODAL);
          break;
        default:
          break;
      }
    },
  );

  const menuItems = useMemo(() => {
    const optionMenus = [
      {
        color: disableColor,
        icon: "export",
        label: "Share",
        size: 20,
        disabled: isStarLoading || isDisable,
        onClick: handleShareAll,
      },
      {
        icon: isMark ? "start-fill" : "star",
        color: isMark
          ? ospThemes.colors.content_brand_primary
          : isStarLoading || isDisable
            ? disableColor
            : ospThemes.colors.content_primary,
        disabled: isStarLoading || isDisable,
        label: "Collect",
        size: 20,
        onClick: handleStarClick,
      },
      {
        icon: "new-chat",
        color: ospThemes.colors.content_primary,
        label: "New Chat",
        size: 20,
        disabled: !messages?.length,
        onClick: onReNewChatClick,
      },
      deviceDetector.isMobile()
        ? undefined
        : {
            icon: "time-five",
            label: "History",
            size: 20,
            onClick: handleHistoryClick,
          },
    ];

    return optionMenus.filter(
      (item): item is NonNullable<typeof item> => item !== undefined,
    );
  }, [
    isMark,
    isStarLoading,
    isDisable,
    disableColor,
    handleStarClick,
    handleShareAll,
    handleHistoryClick,
    setInput,
    handleNewChat,
    !messages?.length,
  ]);

  /**
   * EDG-543 广告承接：点四条问题之一 → `adchat_prompt_tap` + `adchat_first_message_sent`。
   * 非广告问题（`scope !== AD_CHAT_PROMPT`）与非广告会话（landing_id 为空）一条都不发。
   */
  const trackAdChatPromptTap = useMemoizedFn((questionInfo: Question) => {
    if (questionInfo.scope !== AD_CHAT_PROMPT_SCOPE) {
      return;
    }
    const attribution = {
      adSource: getAdSourceCookie(),
      landingId: getLandingIdFromStore(),
    };
    adChatPromptTap({
      ...attribution,
      promptId: questionInfo.id,
      promptSource: "material",
      position: questions?.findIndex((q) => q.id === questionInfo.id) ?? -1,
    });
    adChatFirstMessageSent({ ...attribution, inputType: "prompt" });
  });

  const handleQuestionClick = useMemoizedFn((questionInfo: Question) => {
    // EDG-591 要数据槽：全端直接发送问题原文（PC 也不只是填入输入框），不走推荐问题来源追踪
    if (questionInfo.connectSlot) {
      connectEntry?.markSlotClicked();
      void onSend(questionInfo.question);
      return;
    }
    // 重叠分析配置项（EDG-561 SC-20，后端配置驱动）：不填输入框、不直接发送、不算广告问题点击，
    // 走入口分流；回显气泡文案取该项自身标题，埋点 entry_source 仍为 chat_suggestion
    if (questionInfo.intent === "etf_overlap") {
      void enterOverlap(
        { entry_source: "chat_suggestion" },
        { label: questionInfo.question },
      );
      return;
    }
    // 展示的是问题原文，发出去的是 sendText（广告承接的协议行）；缺省二者相同
    const question = questionInfo.sendText ?? questionInfo.question;
    trackAdChatPromptTap(questionInfo);
    handleQuestionSelect(questionInfo);
    setTrackingValue(question);
    {
      /* 在pc的时候，点击填充到输入框，h5的时候直接发起请求 */
    }
    if (isPc) {
      syncSenderFields(question);
      senderRef.current?.requestFocus();
      return;
    }
    // h5 点击推荐问题直接发送
    Track.log("info", "onChartClick", "[trace][recommend][sender]", {
      arg1: String(messages?.length),
      arg2: String(questionInfo),
    });
    if (messages?.length > 0) {
      const sign = chatInfo?.sign
        ? signArr[1]
          ? `${signArr[0]}:${decodeString(signArr[1])}+ref#${questionInfo.source_id}`
          : `content:chat:id#${questionInfo.source_id}`
        : "";
      const requestParams = {
        type: sign,
        cache_content: questionInfo.prompt || "",
      };
      captureBeforeSend();
      appendMessage({ inputValue: question, customParams: requestParams });
      scrollToBottomOnce();
      return;
    }
    onSend(question);
  });

  const connectSlotShown = Boolean(questions?.[0]?.connectSlot);
  useEffect(() => {
    if (isShowRecommendQuestion && connectSlotShown) {
      connectEntry?.markSlotExposed();
    }
  }, [isShowRecommendQuestion, connectSlotShown, connectEntry]);

  // EDG-591 `+` 菜单：上传直达 P5 并在当前会话发出；连接项发隐藏协议行或定位到已有卡
  const handleConnectMenuUpload = useMemoizedFn(() => {
    void openComposerMenuUpload((question, attachments) => {
      void onSend(question, { attachments });
    });
  });

  const handleConnectMenuConnect = useMemoizedFn(() => {
    if (
      isRequestPortfolioMessage(displayMessages[displayMessages.length - 1])
    ) {
      scrollToBottomOnce("smooth");
      return;
    }
    void onSend(CONNECT_ACCOUNT_SILENT_MESSAGE);
  });

  const senderConnectMenu = useMemo<SenderConnectMenu | undefined>(
    () =>
      connectEntry
        ? {
            hasAccountData: connectEntry.hasAccountData,
            hasPlaidLink: connectEntry.hasPlaidLink,
            trackingParams: buildConnectEntryTrackingParams(connectEntry),
            onUpload: handleConnectMenuUpload,
            onConnect: handleConnectMenuConnect,
          }
        : undefined,
    [connectEntry, handleConnectMenuUpload, handleConnectMenuConnect],
  );

  useEffect(() => {
    if (isPc) {
      return;
    }
    EdgenDrawerCallable.update({ showBgShadow: false });
  }, [isPc]);

  // h5 输入框聚焦（键盘弹起）时补充 mb-4，防止内容被键盘遮挡
  useEffect(() => {
    if (!mobileMode) {
      return;
    }
    const footer = footerRef.current;
    if (!footer) {
      return;
    }
    const onFocusIn = () => {
      setIsInputFocused(true);
      if (!isScrollContainerAtBottom()) {
        return;
      }
      // 无 Bridge 的老 App：仍用双 rAF 滚到底
      if (!keyboardInsets.bridgeEnabled) {
        scrollToBottomOnce();
        return;
      }
      pendingScrollOnKeyboardShowRef.current = true;
      // 键盘未收起就再次聚焦：did-show 可能不再发，双 rAF 在 padding 生效后补滚
      if (keyboardInsets.visible) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!pendingScrollOnKeyboardShowRef.current) {
              return;
            }
            pendingScrollOnKeyboardShowRef.current = false;
            scrollToBottomOnce();
          });
        });
      }
    };
    const onFocusOut = () => {
      setIsInputFocused(false);
      pendingScrollOnKeyboardShowRef.current = false;
    };
    footer.addEventListener("focusin", onFocusIn);
    footer.addEventListener("focusout", onFocusOut);
    return () => {
      footer.removeEventListener("focusin", onFocusIn);
      footer.removeEventListener("focusout", onFocusOut);
    };
  }, [
    mobileMode,
    keyboardInsets.bridgeEnabled,
    keyboardInsets.visible,
    isScrollContainerAtBottom,
    scrollToBottomOnce,
  ]);

  return (
    <div className="relative flex flex-col h-full w-full">
      <div
        className={cn(
          "flex flex-col h-full no-scrollbar",
          sheetListScrollCoordination
            ? "overflow-hidden min-h-0"
            : "overflow-y-auto",
          mobileMode && resolvedHeaderVariant === "modal" && "relative",
          !messages?.length && !isPc && "z-10",
          {
            "transition-all duration-200":
              useWebKeyboardInset && isInputFocused,
            "pb-keyboard-real":
              useWebKeyboardInset &&
              isInputFocused &&
              resolvedHeaderVariant === "tab",
            "pb-keyboard":
              useWebKeyboardInset &&
              isInputFocused &&
              resolvedHeaderVariant === "modal",
          },
        )}
        data-tracking-scope="chat-content"
      >
        {isOnborading && (
          <div className="w-full z-10 bg-background_solid_neutral_secondary flex gap-3 items-center px-4 pt-2 pb-3 md:pt-[150px] md:static">
            <div className="bg-background_brand_overlay20 rounded-full w-8 h-8 flex items-end justify-center pb-1">
              <Icon
                name="ed-chat"
                size={20}
                className="text-content_brand_primary"
              />
            </div>
            <div className="text-content_primary headline_regular_s">
              {I18n.t("Meet Ed")}
            </div>
          </div>
        )}
        {resolvedHeaderVariant === "modal" && !isOnborading && (
          <ChatModalHeaderBar
            isMobile={mobileMode}
            hasMessages={!!messages?.length}
            onClose={onClose as any}
            onNewChatClick={onReNewChatClick}
            moreMenuItemUi={modalMoreMenuItemUi}
            onMoreMenuItemClick={handleModalMoreMenuItemClick}
            menuItems={menuItems}
          />
        )}
        {resolvedHeaderVariant === "tab" && (
          <ChatTabHeader
            moreMenuItemUi={tabMoreMenuItemUi}
            onMoreMenuItemClick={handleTabMoreMenuItemClick}
          />
        )}
        <div
          className={cn("flex-1 overflow-y-auto no-scrollbar px-4 md:px-6", {
            "pb-safe": !isShowRecommendQuestion,
            "pb-[calc(env(safe-area-inset-bottom)+132px)] md:pb-36":
              resolvedFooterVariant !== "hidden",
            "md:px-0 max-w-[900px] md:w-[900px] md:mx-auto":
              resolvedHeaderVariant === "tab",
          })}
          style={
            mobileMode && resolvedHeaderVariant === "modal" && !isOnborading
              ? { paddingTop: CHAT_MODAL_MOBILE_HEADER_HEIGHT_PX }
              : undefined
          }
          ref={scrollContainerRef}
        >
          {themeHandoff && chatId && !messages.length ? (
            handoff?.kind === "etf_overlap" ? (
              <SyncHandoff
                kind="etf_overlap"
                chatId={chatId}
                onReady={submitThemePackHandoff}
              />
            ) : (
              <ThemePackChatHandoff
                themeId={themeHandoffSlug!}
                chatId={chatId}
                onReady={submitThemePackHandoff}
              />
            )
          ) : null}
          <VirtualList
            ref={chatVirtualListRef}
            mode="load-prev"
            hasNextPage={false}
            hasPrevPage={hasChatDetailPreviousPage}
            onStartReached={handleStartReached}
            onStartReachedThreshold="80"
            className={cn(
              "relative flex flex-col w-full h-fit min-h-0",
              !!messages?.length && !isShowRecommendQuestion && "md:pb-0 pb-50", // 避免小屏下内容被输入框遮挡
              !!messages?.length && !isOnborading && "pt-safe", // 避免按钮遮住第一条内容 在 onboarding 页面不需要
            )}
            renderRefresh={
              <PullToRefresh
                isRefreshing
                progress={1}
                className="absolute left-1/2 top-15 z-10 size-9 -translate-x-1/2 rounded-full bg-background_solid_neutral_primary py-2 md:top-2"
              />
            }
          >
            {messageComponents}
          </VirtualList>
          {/* 在pc的时候，只有没有消息时才显示推荐问题；在h5的时候，有对话的时候中也需要展示，再次提问后不再展示 */}
          {isShowRecommendQuestion && (
            <div
              className={cn(
                "w-full h-full flex flex-col items-center justify-center pb-3 md:pb-0",
                {
                  "h-auto mt-[46px] mb-[56px]": messages?.length,
                },
              )}
            >
              <div
                className={cn("flex items-center gap-[2px] mb-4", {
                  hidden: messages?.length,
                })}
              >
                <div className="text-content_primary headline_regular_l">
                  {modalContentText?.title}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 w-full">
                <div className="body_l text-content_primary mb-7 text-center max-w-[200px] mx-auto">
                  {modalContentText?.description}
                </div>
                {questions?.map((question: Question, index) => {
                  const slot = question.connectSlot;
                  const slotTrackingParams = slot
                    ? buildConnectEntryTrackingParams(
                        connectEntry ?? {
                          hasAccountData: undefined,
                          hasPlaidLink: undefined,
                        },
                      )
                    : undefined;
                  const questionItemProps = {
                    question: {
                      ...question,
                      dataTrackingScope: `${question.dataTrackingScope}-chat-modal`,
                    },
                    onClick: (questionItem: Question) => {
                      if (!credits && isLoggedIn) {
                        Track.event("chat_no_credit_question_tap", {
                          question_id: questionItem.id,
                        });
                      }
                      handleQuestionClick(questionItem);
                    },
                    className:
                      "bg-background_white_overlay5 items-start rounded-xl px-3 py-4",
                    textClassName: cn(
                      "!overflow-visible !whitespace-normal",
                      mobileMode && "body_medium_m",
                      {
                        "text-content_brand_primary md:bg-gradient-to-r md:from-background_brand_overlay100 md:to-background_white_overlay100 md:bg-clip-text md:text-transparent":
                          question?.isHighlight && !isPc,
                      },
                    ),
                  };
                  if (!slot || !slotTrackingParams) {
                    return (
                      <QuestionItem
                        data-openreplay-label="chatbox_chatcontent_questionitem"
                        key={question.id || index}
                        {...questionItemProps}
                        dataProps={
                          question.intent === "etf_overlap"
                            ? {
                                "data-or-view": "overlap_entry_view",
                                "data-or-event": "overlap_entry_tap",
                                "data-or-param-entry_source": "chat_suggestion",
                                ...overlapAttributionDataProps(),
                              }
                            : undefined
                        }
                      />
                    );
                  }
                  // 曝光挂外层、点击挂按钮：同一元素不挂两个事件名（tracking-conventions §5 规则 3）
                  return (
                    <div
                      key={question.id || index}
                      {...{
                        "data-or-view": "connect_entry_view",
                        "data-or-param-surface": "suggest_chip",
                        "data-or-param-question": slot.prompt,
                        "data-or-param-category": slot.category,
                        "data-or-param-has_account_data":
                          slotTrackingParams.has_account_data,
                        "data-or-param-has_plaid_link":
                          slotTrackingParams.has_plaid_link,
                      }}
                    >
                      <QuestionItem
                        {...questionItemProps}
                        openReplayLabel="chat_connectslot_open"
                        dataProps={{
                          "data-or-event": "connect_entry_tap",
                          "data-or-param-surface": "suggest_chip",
                          "data-or-param-question": slot.prompt,
                          "data-or-param-category": slot.category,
                          "data-or-param-has_account_data":
                            slotTrackingParams.has_account_data,
                          "data-or-param-has_plaid_link":
                            slotTrackingParams.has_plaid_link,
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10">
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-x-0 bottom-0 z-[9]",
              mobileMode
                ? "h-[calc(env(safe-area-inset-bottom)+108px)]"
                : "h-24",
              {
                hidden: isOnborading,
              },
            )}
            style={{
              background:
                "linear-gradient(180deg, rgba(249, 249, 243, 0.94) 0%, rgba(249, 249, 243, 0.94) 100%)",
            }}
          />
          {/*
           * 「回到底部」悬浮按钮
           * - Mobile（< md）：水平居中，fixed 于消息列表底部上方 52px（top-[-52px]），
           *   mb-safe 补充底部安全区偏移，避免被底部导航遮挡。
           * - Desktop（md+）：定位到右侧（right-6），距底部 112px（bottom-28），
           *   配合 PC 布局中 footer 高度留白。
           * 触发条件：showScrollToBottomIcon === true（距底部超过半屏）
           */}
          {showScrollToBottomIcon && (
            <div
              className={cn(
                "pointer-events-auto absolute left-1/2 -translate-x-1/2 md:left-1/2 md:translate-x-0 top-[-52px] mb-safe md:right-6 md:bottom-28 z-20",
                {
                  "top-[-64px]": isOnborading,
                },
              )}
            >
              <button
                type="button"
                aria-label="Scroll to bottom"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-background_solid_neutral_primary border border-border_white_transparent5"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => scrollToBottomOnce("smooth")}
              >
                <Icon
                  name="chevron-down"
                  size={20}
                  className="text-content_primary"
                />
              </button>
            </div>
          )}
          {!isOnborading && (
            <div
              className={cn(
                "pointer-events-auto sticky md:bottom-0 bottom-4 bg-transparent flex flex-col z-10",
                deviceDetector.isApp()
                  ? "fixed-bottom-safe mx-4 mb-4"
                  : "px-4 pb-4",
                {
                  "md:mb-4":
                    resolvedFooterVariant === "sender" ||
                    (deviceDetector.isApp() && isInputFocused),
                  "pb-4":
                    mobileMode &&
                    (resolvedFooterVariant === "sender" ||
                      (deviceDetector.isApp() && isInputFocused)),
                },
              )}
              id="footer"
              ref={footerRef}
            >
              <div className="w-full mx-auto max-w-[900px] flex flex-col justify-center">
                {shouldShowRenew && (
                  <div
                    className="flex items-center justify-between cursor-pointer mx-3 p-3 bg-background_accent4_overlay20 rounded-t-xl relative"
                    onClick={handleChatRenewClick}
                  >
                    <div className="absolute top-0 left-0 w-full h-full bg-background_solid_neutral_primary rounded-t-xl z-[-1]"></div>
                    <div className="body_medium_s text-content_accent4_primary">
                      {I18n.t("Ed will need a recharge soon...")}
                    </div>
                    <div className="button_s text-content_primary">
                      {I18n.t("Renew")}
                    </div>
                  </div>
                )}
                {(!themeHandoff || messages.length > 0) && (
                  <Sender
                    containerClassName="w-full"
                    onSend={onSend}
                    onStop={stop}
                    loading={
                      !!isLoading && !isHiddenLoadingRef.current && !renewChat
                    }
                    value={displayInput}
                    onChange={handleInputChange}
                    onCloseChatToast={handleCloseChatToast}
                    placeholder="Chat with Ed"
                    chatRefContent={chatRefContent ?? undefined}
                    placeholderColor="var(--edgen-content_quaternary)"
                    className={"rounded-4"}
                    defaultSecondOpinionActive={chatInfo?.secondOpinion}
                    connectMenu={senderConnectMenu}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {hasMounted && (
          <>
            <ChatHistoryModalHydrated
              visible={chatHistoryVisible}
              onClose={closeChatHistory}
              onItemClick={onHistoryItemClick}
              forceHydrate={chatHistoryVisible}
              renameCall={renameCall}
            />
            <RenameModalHydrated ref={renameModalRef} />
          </>
        )}
      </div>
    </div>
  );
});
ChatContent.displayName = "ChatContent";
